const DEFAULT_DURATION = 'GOOD_TILL_CANCEL'
const DEFAULT_SESSION = 'NORMAL'
const TERMINAL_STATUSES = new Set(['FILLED', 'CANCELED', 'CANCELLED', 'EXPIRED', 'REJECTED', 'REPLACED'])

function emptyPlan(reason) {
  return {
    supported: false,
    reason,
    orderSummaries: [],
    gatewayOrders: [],
    stopReplaceOrders: [],
    cancelOrders: [],
    sellOrders: [],
    batches: [],
  }
}

function upper(value) {
  return String(value ?? '').toUpperCase()
}

function accountName(getAccountName, accountNumber) {
  return getAccountName?.(accountNumber) || `****${String(accountNumber).slice(-4)}`
}

function isActiveStatus(status) {
  const normalized = upper(status)
  return normalized === '' || !TERMINAL_STATUSES.has(normalized)
}

function isSellSide(order) {
  return upper(order?.side ?? order?.instruction).includes('SELL')
}

function matchesSymbol(order, symbol) {
  return upper(order?.symbol) === upper(symbol)
}

function getTradeQuantity(trade) {
  return Number(trade?.quantity_open || trade?.quantity_total) || 0
}

function getStopOrderId(stop) {
  return stop?.orderId ?? stop?.existingOrderId
}

function getStopQuantity(stop, trade, allowTradeQuantityFallback) {
  const stopQuantity = Number(stop?.quantity) || 0
  if (stopQuantity > 0) return stopQuantity
  if (!allowTradeQuantityFallback) return 0
  return Number(trade?.quantity_open || trade?.quantity_total) || 0
}

function selectedStopRows(stopOrders, selectedStopPrices) {
  if (!(selectedStopPrices instanceof Set) || selectedStopPrices.size === 0) return stopOrders
  const groups = new Set([...selectedStopPrices].map(price => Number(price).toFixed(2)))
  return stopOrders.filter(stop => groups.has(Number(stop.stopPrice).toFixed(2)))
}

export function buildMoveStopReplacePlan({
  symbol,
  openTrades = [],
  stopOrders = [],
  selectedStopPrices = null,
  newStopPrice,
  allowTradeQuantityFallback = false,
  getAccountName,
} = {}) {
  const price = Number(newStopPrice)
  if (!symbol) return emptyPlan('Missing symbol')
  if (!Number.isFinite(price) || price <= 0) return emptyPlan('Missing valid stop price')
  if (!Array.isArray(stopOrders) || stopOrders.length === 0) return emptyPlan('No active stop orders found')

  const tradesByAccount = new Map(openTrades.map(trade => [String(trade.account_number), trade]))
  const stopsToMove = selectedStopRows(stopOrders, selectedStopPrices)
  if (stopsToMove.length === 0) return emptyPlan('No selected stop orders found')

  const orderSummaries = []
  const gatewayOrders = []

  for (const stop of stopsToMove) {
    const accountNumber = String(stop?.accountNumber ?? '')
    if (!accountNumber) return emptyPlan('Selected stop is missing account number')
    if (!getStopOrderId(stop)) return emptyPlan('Selected stop is missing order id')
    if (!Number.isFinite(Number(stop.stopPrice))) return emptyPlan('Selected stop is missing stop price')

    const trade = tradesByAccount.get(accountNumber)
    const quantity = getStopQuantity(stop, trade, allowTradeQuantityFallback)
    if (quantity <= 0) return emptyPlan('Selected stop is missing quantity')

    orderSummaries.push({
      accountName: accountName(getAccountName, accountNumber),
      accountNumber,
      quantity,
      oldStop: stop.stopPrice,
      newStop: price,
    })

    gatewayOrders.push({
      account_number: accountNumber,
      symbol: upper(symbol),
      side: 'SELL',
      quantity,
      orderType: 'STOP',
      stopPrice: price,
      duration: DEFAULT_DURATION,
      session: DEFAULT_SESSION,
      assetType: 'EQUITY',
      existingOrderId: getStopOrderId(stop),
    })
  }

  return {
    supported: true,
    reason: null,
    orderSummaries,
    gatewayOrders,
    stopReplaceOrders: [],
    cancelOrders: [],
    sellOrders: [],
    batches: [],
  }
}

function activeStopsForAccount(stopOrders, accountNumber) {
  return (Array.isArray(stopOrders) ? stopOrders : [])
    .filter(stop => String(stop?.accountNumber ?? '') === String(accountNumber))
    .filter(stop => isActiveStatus(stop?.status))
    .sort((a, b) => Number(b.stopPrice) - Number(a.stopPrice))
}

function buildStopReplaceOrder({ symbol, accountNumber, stop, quantity }) {
  return {
    account_number: accountNumber,
    symbol: upper(symbol),
    side: 'SELL',
    quantity,
    orderType: 'STOP',
    stopPrice: Number(stop.stopPrice),
    duration: DEFAULT_DURATION,
    session: DEFAULT_SESSION,
    assetType: 'EQUITY',
    existingOrderId: getStopOrderId(stop),
  }
}

function orderCancelId(order, fallbackParent = null) {
  return order?.orderId ?? order?.existingOrderId ?? fallbackParent?.orderId ?? fallbackParent?.existingOrderId ?? null
}

function addCancelOrder(cancelByKey, { symbol, accountNumber, orderId }) {
  const acct = String(accountNumber ?? '')
  const id = String(orderId ?? '')
  if (!acct || !id) return false
  cancelByKey.set(`${acct}|${id}`, {
    account_number: acct,
    symbol: upper(symbol),
    existingOrderId: id,
  })
  return true
}

function orderChildren(order) {
  const rawChildren = order?.childOrderStrategies || order?._raw?.childOrderStrategies
  return [
    ...(Array.isArray(order?.groupedOrders) ? order.groupedOrders : []),
    ...(Array.isArray(order?.childOrders) ? order.childOrders : []),
    ...(Array.isArray(rawChildren) ? rawChildren : []),
  ]
}

function liveSellLimitExitOrders({ symbol, accountSet, rawOrders }) {
  const out = []
  if (!Array.isArray(rawOrders)) return out
  const targetSymbol = upper(symbol)
  const seen = new Set()

  const visit = (order, inherited = {}) => {
    if (!order || seen.has(order)) return
    seen.add(order)

    const resolvedSymbol = upper(order?.symbol ?? inherited.symbol)
    const resolvedAccount = String(order?.accountNumber ?? order?.account_number ?? inherited.accountNumber ?? '')
    const status = upper(order?.status ?? inherited.status)
    const orderType = upper(order?.orderType)
    const side = upper(order?.side ?? order?.instruction ?? inherited.side)
    const accountMatches = accountSet.size === 0 || accountSet.has(resolvedAccount)

    if (
      orderType === 'LIMIT' &&
      resolvedSymbol === targetSymbol &&
      accountMatches &&
      side.includes('SELL') &&
      isActiveStatus(status)
    ) {
      out.push({
        accountNumber: resolvedAccount,
        orderId: orderCancelId(order, inherited.parentOrder),
        symbol: targetSymbol,
      })
    }

    for (const child of orderChildren(order)) {
      visit(child, {
        symbol: resolvedSymbol,
        accountNumber: resolvedAccount,
        status,
        side,
        parentOrder: order,
      })
    }
  }

  for (const order of rawOrders) {
    visit(order)
  }
  return out
}

export function buildTakeProfitScaleOutPlan({
  symbol,
  openTrades = [],
  rawOrders = [],
  stopOrders = [],
  getAccountName,
} = {}) {
  if (!symbol) return emptyPlan('Missing symbol')
  if (!Array.isArray(openTrades) || openTrades.length === 0) return emptyPlan('No open trades found')

  const eligibleTrades = openTrades.filter(trade => Math.floor(getTradeQuantity(trade) / 3) > 0)
  if (eligibleTrades.length === 0) {
    return emptyPlan('All accounts have fewer than 3 shares — cannot take 1/3.')
  }

  const accountSet = new Set(eligibleTrades.map(trade => String(trade?.account_number ?? '')).filter(Boolean))
  const cancelByKey = new Map()
  for (const target of liveSellLimitExitOrders({ symbol, accountSet, rawOrders })) {
    if (!addCancelOrder(cancelByKey, {
      symbol,
      accountNumber: target.accountNumber,
      orderId: target.orderId,
    })) {
      return emptyPlan('Live sell limit exit is missing cancel id')
    }
  }

  const orderSummaries = []
  const stopReplaceOrders = []
  const sellOrders = []

  for (const trade of eligibleTrades) {
    const accountNumber = String(trade?.account_number ?? '')
    if (!accountNumber) return emptyPlan('Open trade is missing account number')

    const totalQty = getTradeQuantity(trade)
    const sellQty = Math.floor(totalQty / 3)

    const remainingQty = totalQty - sellQty
    const accountStops = activeStopsForAccount(stopOrders, accountNumber)
    let currentCoverage = 0
    for (const stop of accountStops) {
      if (!getStopOrderId(stop)) return emptyPlan('Active stop is missing order id')
      if (!Number.isFinite(Number(stop.stopPrice))) return emptyPlan('Active stop is missing stop price')
      const stopQty = Number(stop.quantity) || 0
      if (stopQty <= 0) return emptyPlan('Active stop is missing quantity')
      currentCoverage += stopQty
    }

    let sharesToRelieve = Math.max(0, currentCoverage - remainingQty)
    const stopAdjustments = []
    let projectedCoverage = 0
    for (const stop of accountStops) {
      const oldQty = Number(stop.quantity) || 0
      const reduceBy = Math.min(oldQty, sharesToRelieve)
      const newQty = oldQty - reduceBy
      sharesToRelieve -= reduceBy
      projectedCoverage += newQty

      if (reduceBy > 0) {
        stopAdjustments.push({
          orderId: getStopOrderId(stop),
          stopPrice: Number(stop.stopPrice),
          oldQty,
          newQty,
          reduceBy,
          action: newQty > 0 ? 'REPLACE' : 'CANCEL',
        })
        if (newQty > 0) {
          stopReplaceOrders.push(buildStopReplaceOrder({
            symbol,
            accountNumber,
            stop,
            quantity: newQty,
          }))
        } else {
          const cancelId = stop?.isBracket && stop?.parentOrderId ? stop.parentOrderId : getStopOrderId(stop)
          if (!addCancelOrder(cancelByKey, { symbol, accountNumber, orderId: cancelId })) {
            return emptyPlan('Depleted stop leg is missing cancel id')
          }
        }
      }
    }

    if (sharesToRelieve !== 0) return emptyPlan('Unable to allocate sell quantity across active stops')
    if (projectedCoverage > remainingQty) {
      return emptyPlan(
        `Projected stop coverage (${projectedCoverage}) exceeds remaining quantity (${remainingQty}) for account ${accountNumber}`
      )
    }

    sellOrders.push({
      account_number: accountNumber,
      symbol: upper(symbol),
      side: 'SELL',
      quantity: sellQty,
      orderType: 'MARKET',
      duration: 'DAY',
      session: DEFAULT_SESSION,
      assetType: 'EQUITY',
    })

    orderSummaries.push({
      accountName: accountName(getAccountName, accountNumber),
      accountNumber,
      quantity: sellQty,
      totalQuantity: totalQty,
      remainingQuantity: remainingQty,
      side: 'SELL',
      orderType: 'MARKET',
      stopAdjustments,
    })
  }

  if (sellOrders.length === 0) {
    return emptyPlan('All accounts have fewer than 3 shares — cannot take 1/3.')
  }
  const cancelOrders = [...cancelByKey.values()]
  const batches = []
  if (cancelOrders.length > 0) batches.push({ action: 'CANCEL', orders: cancelOrders })
  if (stopReplaceOrders.length > 0) batches.push({ action: 'REPLACE', orders: stopReplaceOrders })
  batches.push({ action: 'PLACE', orders: sellOrders })

  return {
    supported: true,
    reason: null,
    orderSummaries,
    gatewayOrders: [],
    stopReplaceOrders,
    cancelOrders,
    sellOrders,
    batches,
  }
}

function liveLimitExitOrders({ symbol, accountSet, rawOrders }) {
  if (!Array.isArray(rawOrders)) return []
  return rawOrders.filter(order => {
    if (!order || order.orderType !== 'LIMIT') return false
    if (!matchesSymbol(order, symbol)) return false
    if (!accountSet.has(String(order.accountNumber ?? ''))) return false
    if (!isSellSide(order)) return false
    if (!isActiveStatus(order.status)) return false
    return order.promotedFromBracket === true || order.isBracket === true || order.parentOrderId != null
  })
}

export function buildCloseFullPositionPlan({
  symbol,
  openTrades = [],
  rawOrders = [],
  stopOrders = [],
  getAccountName,
} = {}) {
  if (!symbol) return emptyPlan('Missing symbol')
  if (!Array.isArray(openTrades) || openTrades.length === 0) return emptyPlan('No open trades found')

  const accountSet = new Set(openTrades.map(trade => String(trade.account_number)).filter(Boolean))
  const cancelByKey = new Map()

  const addCancel = (accountNumber, orderId) => {
    const acct = String(accountNumber ?? '')
    const id = String(orderId ?? '')
    if (!acct || !id) return false
    cancelByKey.set(`${acct}|${id}`, {
      account_number: acct,
      symbol: upper(symbol),
      existingOrderId: id,
    })
    return true
  }

  for (const stop of Array.isArray(stopOrders) ? stopOrders : []) {
    const accountNumber = String(stop?.accountNumber ?? '')
    if (!accountSet.has(accountNumber)) continue
    const cancelId = stop?.isBracket && stop?.parentOrderId ? stop.parentOrderId : stop?.orderId
    if (!addCancel(accountNumber, cancelId)) return emptyPlan('Live stop exit is missing cancel id')
  }

  for (const order of liveLimitExitOrders({ symbol, accountSet, rawOrders })) {
    if (!addCancel(order.accountNumber, order.orderId)) return emptyPlan('Live limit exit is missing cancel id')
  }

  const cancelOrders = [...cancelByKey.values()]
  if (cancelOrders.length === 0) return emptyPlan('No live exit orders found to cancel')

  const orderSummaries = []
  const sellOrders = []

  for (const trade of openTrades) {
    const accountNumber = String(trade.account_number ?? '')
    if (!accountNumber) return emptyPlan('Open trade is missing account number')
    const quantity = getTradeQuantity(trade)
    if (quantity <= 0) continue

    sellOrders.push({
      account_number: accountNumber,
      symbol: upper(symbol),
      side: 'SELL',
      quantity,
      orderType: 'MARKET',
      duration: 'DAY',
      session: DEFAULT_SESSION,
      assetType: 'EQUITY',
    })

    const accountCancels = cancelOrders.filter(order => order.account_number === accountNumber)
    orderSummaries.push({
      accountName: accountName(getAccountName, accountNumber),
      accountNumber,
      quantity,
      side: 'SELL',
      orderType: 'MARKET',
      stopsToCancel: accountCancels.length,
      stopPrices: (Array.isArray(stopOrders) ? stopOrders : [])
        .filter(stop => String(stop?.accountNumber ?? '') === accountNumber)
        .map(stop => stop.stopPrice),
    })
  }

  if (sellOrders.length === 0) return emptyPlan('No shares to sell')

  return {
    supported: true,
    reason: null,
    orderSummaries,
    gatewayOrders: [],
    cancelOrders,
    sellOrders,
    batches: [
      { action: 'CANCEL', orders: cancelOrders },
      { action: 'PLACE', orders: sellOrders },
    ],
  }
}
